/**
 * County Marriage Certificate Component
 * Displays a Traditional Marriage Certificate
 */

import React, { useEffect, useState } from "react";
import countrySeal from "../assets/images/country-seal.jpg";
import mlgLogo from "../assets/images/mlg-logo.png";
import { certificateService } from "../services/api/certificate.service";
import { paymentService } from "../services/api/payment.service";
import { UserStatusId } from "../types/user.types";
import { getFullPath } from "../utils/getFullPath";

export interface CountyMarriageCertProps {
  groomName?: string;
  groomDateOfBirth?: string;
  groomPlaceOfBirth?: string;
  groomCountyOfBirth?: string;
  groomFatherName?: string;
  groomMotherName?: string;
  brideName?: string;
  brideProposedName?: string;
  brideDateOfBirth?: string;
  bridePlaceOfBirth?: string;
  brideCountyOfBirth?: string;
  brideFatherName?: string;
  brideMotherName?: string;
  placeOfWedding?: string;
  dateOfWedding?: string;
  countyName?: string;
  certificateCode?: string;
  revenueNumber?: string;
  ministryName?: string;
  groomPhotoUrl?: string;
  bridePhotoUrl?: string;
  certificateApplicationId?: number;
}

export const CountyMarriageCert: React.FC<CountyMarriageCertProps> = ({
  groomName,
  groomDateOfBirth,
  groomPlaceOfBirth,
  groomCountyOfBirth,
  groomFatherName,
  groomMotherName,
  brideName,
  brideProposedName,
  brideDateOfBirth,
  bridePlaceOfBirth,
  brideCountyOfBirth,
  brideFatherName,
  brideMotherName,
  placeOfWedding = "",
  dateOfWedding = "",
  countyName = "",
  certificateCode = "",
  revenueNumber = "",
  ministryName = "",
  groomPhotoUrl,
  bridePhotoUrl,
  certificateApplicationId,
}) => {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return { day: "", month: "", year: "" };
    try {
      const date = new Date(dateStr);
      return {
        day: date.getDate().toString(),
        month: date.toLocaleDateString("en-US", { month: "long" }),
        year: date.getFullYear().toString(),
      };
    } catch {
      return { day: "", month: "", year: "" };
    }
  };

  const groomDOB = formatDate(groomDateOfBirth || "");
  const brideDOB = formatDate(brideDateOfBirth || "");
  const weddingDate = formatDate(dateOfWedding);

  const cleanGroomName = groomName || "";
  const cleanGroomFatherName = groomFatherName || "";
  const cleanGroomMotherName = groomMotherName || "";
  const cleanBrideName = brideName || "";
  const cleanBrideFatherName = brideFatherName || "";
  const cleanBrideMotherName = brideMotherName || "";
  const [paymentRevenueNumber, setPaymentRevenueNumber] = useState("");
  const [paymentReferenceNumber, setPaymentReferenceNumber] = useState("");

  // Get full photo URLs
  const groomPhotoFullUrl = getFullPath(groomPhotoUrl);
  const bridePhotoFullUrl = getFullPath(bridePhotoUrl);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!certificateApplicationId) {
        setPaymentRevenueNumber(revenueNumber || "");
        setPaymentReferenceNumber("");
        return;
      }

      try {
        const payments = await paymentService.getPaymentsByApplicationId(
          certificateApplicationId,
        );
        const payment = Array.isArray(payments)
          ? payments.find((p) => p.revenueNumber || p.paymentReferenceNumber) ||
            payments[0]
          : undefined;

        setPaymentRevenueNumber(payment?.revenueNumber || revenueNumber || "");
        setPaymentReferenceNumber(payment?.paymentReferenceNumber || "");
      } catch (err) {
        console.error("Error fetching payment details:", err);
        setPaymentRevenueNumber(revenueNumber || "");
        setPaymentReferenceNumber("");
      }
    };

    fetchPaymentDetails();
  }, [certificateApplicationId, revenueNumber]);

  // Handle print event to update status to Printed
  useEffect(() => {
    const handleBeforePrint = async () => {
      if (certificateApplicationId) {
        try {
          await certificateService.changeStatus(
            certificateApplicationId,
            UserStatusId.Printed,
          );
          console.log(
            `Certificate application ${certificateApplicationId} status updated to Printed`,
          );
        } catch (error) {
          console.error(
            "Failed to update certificate status to Printed:",
            error,
          );
        }
      }
    };

    // Listen for print events
    window.addEventListener("beforeprint", handleBeforePrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, [certificateApplicationId]);

  return (
    <div
      style={{
        width: "297mm",
        minHeight: "210mm",
        margin: "0 auto",
        padding: "15mm",
        background: "#d4d4d4",
        fontFamily: "'Times New Roman', serif",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Decorative Border */}
      <div
        style={{
          position: "absolute",
          top: "10mm",
          left: "10mm",
          right: "10mm",
          bottom: "10mm",
          border: "8px solid #2c2c2c",
          borderRadius: "4px",
          background: "#c8c8c8",
          borderImage:
            "repeating-linear-gradient(45deg, #2c2c2c 0px, #2c2c2c 10px, transparent 10px, transparent 20px, #2c2c2c 20px, #2c2c2c 30px, transparent 30px, transparent 40px) 8",
        }}
      >
        {/* Inner decorative pattern */}
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            right: "12px",
            bottom: "12px",
            border: "8px solid #2c2c2c",
            background: "linear-gradient(to bottom, #ffffff, #ffffff)",
            padding: "25px",
            borderImage:
              "repeating-linear-gradient(45deg, #2c2c2c 0px, #2c2c2c 10px, transparent 10px, transparent 20px, #2c2c2c 20px, #2c2c2c 30px, transparent 30px, transparent 40px) 8",
          }}
        >
          {/* Main Certificate Table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "15px",
            }}
          >
            <tbody>
              {/* Header Section */}
              <tr>
                <td
                  style={{
                    width: "15%",
                    verticalAlign: "top",
                    padding: "10px 5px",
                  }}
                >
                  <img
                    src={countrySeal || undefined}
                    alt="Country Seal"
                    style={{
                      width: "80px",
                      height: "80px",
                      margin: "0 auto",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                  <div
                    style={{
                      marginTop: "6px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {certificateCode || ""}
                  </div>
                  <div
                    style={{
                      marginTop: "3px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    LRA REF NO. {paymentRevenueNumber || ""}
                  </div>
                  <div
                    style={{
                      marginTop: "2px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    PAYMENT REF. {paymentReferenceNumber || ""}
                  </div>
                </td>
                <td style={{ textAlign: "center", paddingBottom: "10px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "15px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td colSpan={3}>
                          <h1
                            style={{
                              fontSize: "24px",
                              fontWeight: "bold",
                              margin: "10px 0",
                              letterSpacing: "2px",
                              textTransform: "uppercase",
                            }}
                          >
                            {ministryName}
                          </h1>
                        </td>

                        <td></td>
                      </tr>
                      <tr>
                        <td>
                          {groomPhotoFullUrl ? (
                            <img
                              src={groomPhotoFullUrl || undefined}
                              alt="Groom"
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                margin: "0 auto",
                                display: "block",
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.style.display = "none";
                                const parent = img.parentElement;
                                if (parent) {
                                  const placeholder =
                                    document.createElement("div");
                                  placeholder.style.cssText =
                                    "width: 90px; height: 90px; border: 0px solid #999; background-color: #6c6d7c; margin: 0 auto;";
                                  parent.appendChild(placeholder);
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                backgroundColor: "#6c6d7c",
                                margin: "0 auto",
                              }}
                            >
                              {/* Placeholder for groom's photo */}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                          }}
                        >
                          <p style={{ margin: "3px 0" }}>
                            Capitol Hill, Executive Mansion Grounds
                          </p>
                          <p style={{ margin: "3px 0" }}>P.O. Box 9008</p>
                          <p style={{ margin: "3px 0" }}>
                            1000 Monrovia, 10 Liberia
                          </p>
                          <p style={{ margin: "3px 0" }}>West Africa</p>
                        </td>
                        <td>
                          {bridePhotoFullUrl ? (
                            <img
                              src={bridePhotoFullUrl || undefined}
                              alt="Bride"
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                margin: "0 auto",
                                display: "block",
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.style.display = "none";
                                const parent = img.parentElement;
                                if (parent) {
                                  const placeholder =
                                    document.createElement("div");
                                  placeholder.style.cssText =
                                    "width: 90px; height: 90px; border: 0px solid #999; background-color: #6c6d7c; margin: 0 auto;";
                                  parent.appendChild(placeholder);
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                backgroundColor: "#6c6d7c",
                                margin: "0 auto",
                              }}
                            >
                              {/* Placeholder for bride's photo */}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
                <td
                  style={{
                    width: "15%",
                    verticalAlign: "top",
                    padding: "10px 5px",
                  }}
                >
                  <img
                    src={mlgLogo || undefined}
                    alt="MLG Logo"
                    style={{
                      width: "80px",
                      height: "80px",
                      margin: "0 auto",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </td>
              </tr>

              {/* Certificate Body - Legal Text */}
              <tr>
                <td
                  colSpan={3}
                  style={{
                    padding: "10px 0",
                    textAlign: "justify",
                    lineHeight: "2.25",
                    fontSize: "14px",
                  }}
                >
                  <p style={{ margin: "10px 0" }}>
                    <strong>THIS IS TO CERTIFY</strong> that in accordance with
                    the <strong>Customary Laws</strong> and the{" "}
                    <strong>New Inheritance Law of 1998</strong> of the Republic
                    of Liberia,{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanGroomName}
                    </span>{" "}
                    born on{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {groomDOB.day}
                    </span>{" "}
                    day of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {groomDOB.month}
                    </span>
                    , A.D.{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {groomDOB.year}
                    </span>{" "}
                    in the Town/City of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {groomPlaceOfBirth}
                    </span>
                    {", "}
                    County of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {groomCountyOfBirth}
                    </span>{" "}
                    unto the union of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanGroomFatherName}
                    </span>{" "}
                    and{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanGroomMotherName}
                    </span>{" "}
                    and{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanBrideName}
                    </span>{" "}
                    born on{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {brideDOB.day}
                    </span>{" "}
                    day of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {brideDOB.month}
                    </span>
                    , A.D.{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {brideDOB.year}
                    </span>{" "}
                    in the Town/City of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {bridePlaceOfBirth}
                    </span>
                    , County of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {brideCountyOfBirth}
                    </span>{" "}
                    unto the union of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanBrideFatherName}
                    </span>{" "}
                    and{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {cleanBrideMotherName}
                    </span>{" "}
                    have on this{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.day}
                    </span>{" "}
                    day of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.month}
                    </span>{" "}
                    A.D.{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.year}
                    </span>{" "}
                    in the City/Township/Town of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {placeOfWedding}
                    </span>
                    , County of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {countyName}
                    </span>{" "}
                    entered into <strong>Traditional Marriage</strong> and have
                    now been declared as <strong>husband and wife</strong>{" "}
                    effective as of today's date. They shall be known as{" "}
                    {brideProposedName ? (
                      <>
                        <span
                          style={{
                            textDecoration: "underline",
                            fontFamily: "'Dancing Script', cursive",
                          }}
                        >
                          {`Mr. ${cleanGroomName}`}
                        </span>{" "}
                        and{" "}
                        <span
                          style={{
                            textDecoration: "underline",
                            fontFamily: "'Dancing Script', cursive",
                          }}
                        >
                          {`${brideProposedName}`}
                        </span>
                      </>
                    ) : (
                      <span
                        style={{
                          textDecoration: "underline",
                          fontFamily: "'Dancing Script', cursive",
                        }}
                      >
                        {`Mr. and Mrs. ${cleanGroomName}`}
                      </span>
                    )}
                    . Given under our hands and seal, this{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.day}
                    </span>{" "}
                    day of{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.month}
                    </span>
                    , A.D.{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {weddingDate.year}.
                    </span>
                  </p>
                </td>
              </tr>

              <tr>
                <td colSpan={3}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td style={{ width: "25%", padding: "10px" }}>
                          <div
                            style={{
                              borderBottom: "1px solid #000",
                              minHeight: "40px",
                              marginBottom: "5px",
                            }}
                          ></div>
                          <p
                            style={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            HUSBAND
                          </p>
                        </td>
                        <td style={{ width: "25%", padding: "10px" }}>
                          <div
                            style={{
                              borderBottom: "1px solid #000",
                              minHeight: "40px",
                              marginBottom: "5px",
                            }}
                          ></div>
                          <p
                            style={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            PARENTS/GUARDIAN
                          </p>
                        </td>
                        <td style={{ width: "25%", padding: "10px" }}>
                          <div
                            style={{
                              borderBottom: "1px solid #000",
                              minHeight: "40px",
                              marginBottom: "5px",
                            }}
                          ></div>
                          <p
                            style={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            WIFE
                          </p>
                        </td>
                        <td>
                          <div
                            style={{
                              borderBottom: "1px solid #000",
                              minHeight: "40px",
                              marginBottom: "5px",
                            }}
                          ></div>
                          <p
                            style={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            PARENTS/GUARDIAN
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>

              {/* Official Approval Section */}
              <tr>
                <td colSpan={3}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                      marginTop: "40px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            width: "33%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Prepared:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-block",
                                minWidth: "200px",
                              }}
                            >
                              {" "}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            PARAMOUNT CHIEF
                          </p>
                        </td>

                        <td
                          style={{
                            width: "33%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Verified/Attested:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-block",
                                minWidth: "200px",
                              }}
                            >
                              {" "}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            COUNTY CITY MAYOR
                          </p>
                        </td>
                        <td
                          style={{
                            width: "34%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Approved:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-block",
                                minWidth: "230px",
                              }}
                            >
                              {" "}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            COUNTY SUPERINTENDENT
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              <tr style={{ textAlign: "left" }}>
                <td
                  colSpan={3}
                  style={{ padding: "1px 0 0 0", textAlign: "left" }}
                >
                  {certificateCode ? (
                    <img
                      src={
                        getFullPath(
                          `https://api.qrserver.com/v1/create-qr-code/?size=75x75&data=${encodeURIComponent(`CODE:${certificateCode}|LRA:${paymentRevenueNumber}|PAYMENT:${paymentReferenceNumber}`)}`,
                        ) || undefined
                      }
                      alt={`QR Code: ${certificateCode}`}
                      style={{
                        width: "75px",
                        height: "75px",
                        border: "1px solid #433d3d",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "75px",
                        height: "75px",
                        border: "1px solid #433d3d",
                        display: "inline-block",
                      }}
                    >
                      {/* Placeholder for QR Code */}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CountyMarriageCert;
